import {Skeleton} from '@material-ui/lab';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React from 'react';
import {Link} from 'react-router-dom';
import TimeAgo from 'react-timeago';

import CreateProjectDrawer from '../../pages/HomePage/HomePageSidebar/CreateProjectDrawer';
import {
  AccessOption,
  AccessOptions,
  getDisplayNameForProjectAccess,
  getIconForProjectAccess,
  getProjectAccess,
} from '../../util/permissions';
import {useRampFlagStarredProjects} from '../../util/rampFeatureFlags';
import {StarredProjectsContext} from '../../util/StarredProjectsContextProvider';
import {ProjectActionMenu} from '../Project/ProjectActionMenu';
import {
  PageInfo,
  ProjectsByAccountQuery,
  ProjectsByTeamQuery,
  ProjectsByUserQuery,
} from './../../generated/graphql';
import {
  makePaginatedList,
  PaginatedListProps,
  sortDirections,
  SortOperators,
} from './PaginatedList';
import {
  PROJECTS_BY_ACCOUNT_QUERY,
  PROJECTS_BY_TEAM_QUERY,
  PROJECTS_BY_USER_QUERY,
} from './projectQuery.query';

const PROJECT_COLUMN_STYLE_WITH_STARS =
  'grid-cols-[2fr_1fr_1fr_.5fr_20px_30px]';
const PROJECT_COLUMN_STYLE_NO_STARS = 'grid-cols-[2fr_1fr_1fr_.5fr_30px]';
const USER_PROJECT_COLUMN_STYLE_WITH_STARS =
  'grid-cols-[2fr_1fr_1.4fr_.5fr_2fr_20px_30px]';
const USER_PROJECT_COLUMN_STYLE_NO_STARS =
  'grid-cols-[2fr_1fr_1.4fr_.5fr_2fr_30px]';

type EmptyProjectsProps = {
  isCreatingProject: boolean;
  toggleCreateProjectDrawer: () => void;
  defaultTeam: string;
  isTeam: boolean;
  isReadOnly: boolean;
};

const EmptyProjectsComp = ({
  isCreatingProject,
  toggleCreateProjectDrawer,
  defaultTeam,
  isTeam,
  isReadOnly,
}: EmptyProjectsProps) => {
  return (
    <Tailwind>
      <div className="my-32 flex flex-col items-center">
        <div className="mb-8 text-2xl font-semibold	">No projects yet </div>
        <div className="mb-24 text-center text-lg font-normal leading-[1.575rem] text-moon-650">
          This {isTeam ? ' team ' : ' profile '} does not have projects yet.
          They are a great way to collaborate on projects. <br /> You can get
          started by creating a project.
        </div>
        {!isReadOnly && (
          <>
            <Button icon="add-new" onClick={toggleCreateProjectDrawer}>
              Create project
            </Button>
            <CreateProjectDrawer
              open={isCreatingProject}
              entityName={defaultTeam}
              toggleProjectDrawer={toggleCreateProjectDrawer}
            />
          </>
        )}
      </div>
    </Tailwind>
  );
};

const ProjectListMeta: PaginatedListProps<
  ProjectsByTeamQuery,
  ListItem
>['Components']['Meta'] = ({
  fetchItems,
  hasNextPage,
  hasPreviousPage,
  listSize,
  searchPattern,
  setSearchPattern,
}) => {
  const buttonClasses = classNames(
    'justify-center overflow-hidden mr-4 flex items-center rounded-[100%] p-4 hover:bg-[#f4f4f4] focus:bg-[#f4f4f4] focus:outline-none disabled:hover:bg-transparent disabled:text-[#d2d2d2]'
  );
  return (
    <div className="flex items-center border-b-2 border-solid border-[rgb(228_230_235)] px-12 py-6">
      <Icon className="mr-4 h-[16px] text-[#363a3d]" name="search" />
      <input
        className="mr-20 block flex-auto px-4 py-6 text-[13px] focus:outline-teal-400"
        onChange={e => setSearchPattern(e.target.value)}
        placeholder="Search by project name"
        value={searchPattern}
        type="text"
      />
      <div className="mr-12 text-[13px]">
        {listSize
          ? `showing ${listSize} ${
              hasNextPage || hasPreviousPage ? 'of many' : ''
            }`
          : ''}
      </div>
      <button
        className={buttonClasses}
        disabled={!hasPreviousPage}
        onClick={e => {
          fetchItems('prev');
        }}
        type="button">
        <Icon className="rotate-90" name="chevron-down" />
      </button>
      <button
        className={buttonClasses}
        disabled={!hasNextPage}
        onClick={e => {
          fetchItems('next');
        }}
        type="button">
        <Icon className="rotate-90" name="chevron-up" />
      </button>
    </div>
  );
};

const ProjectListWrapper = (props: any) => (
  <section
    className="mb-40 overflow-hidden rounded border-[1px] border-solid border-[#d2d2d2] bg-white"
    {...props}
  />
);

const LastActiveDate = (lastActive: string) => {
  if (!lastActive) {
    return null;
  }
  const runDate = new Date(lastActive);

  const dayOld = Date.now() - 24 * 60 * 60 * 1000;
  if (runDate.getTime() > dayOld) {
    return <TimeAgo date={runDate} />;
  }

  const runDateString = runDate.toISOString().substring(0, 10);
  return <span>{runDateString}</span>;
};

const ProjectListHeader = ({
  sortOrder,
  setSortOrder,
}: {
  sortOrder: [SortOperators | null, string];
  setSortOrder: any;
}) => {
  const [sortDir, sortBy] = sortOrder;
  const columns = [
    {displayName: 'Name', orderBy: 'name', isActive: true},
    {displayName: 'Last Run', orderBy: 'last_active', isActive: true},
    {displayName: 'Project Visibility', orderBy: 'access', isActive: true},
    // ordering by total_runs is not supported in the backend so this cannot be set to active
    {displayName: 'Runs', orderBy: 'total_runs', isActive: false},
  ];
  const isStarredProjectsEnabled = useRampFlagStarredProjects();
  const gridColsStyle = isStarredProjectsEnabled
    ? PROJECT_COLUMN_STYLE_WITH_STARS
    : PROJECT_COLUMN_STYLE_NO_STARS;

  return (
    <div
      className={classNames(
        gridColsStyle,
        'grid h-[32px] items-center gap-x-16 bg-[#f4f4f4] px-[9px] py-5 text-sm capitalize'
      )}>
      {columns.map((col, i) => {
        const isActiveCol = sortBy === col.orderBy;
        return (
          <button
            className={`flex p-3 text-left focus:font-bold focus:outline-none ${
              isActiveCol && 'font-bold'
            }`}
            disabled={!col.isActive}
            key={`project-list-header-${i}`}
            role="columnheader"
            onClick={e => {
              const newSortDir =
                sortDir === sortDirections.asc
                  ? sortDirections.desc
                  : sortDirections.asc;
              setSortOrder([newSortDir, col.orderBy]);
            }}>
            <span>{col.displayName}</span>{' '}
            {isActiveCol ? (
              sortDir === sortDirections.asc ? (
                <Icon height={18} name="sort-ascending" />
              ) : (
                <Icon name="sort-descending" />
              )
            ) : null}
          </button>
        );
      })}

      <div></div>
    </div>
  );
};
const ProjectListItem = ({
  isLoading,
  itemData,
  onDeleted,
}: {
  isLoading: boolean;
  itemData: ListItem;
  onDeleted?: () => void;
}) => {
  const projectAccess = getProjectAccess(
    itemData.access ?? AccessOption.Private
  );
  const isStarredProjectsEnabled = useRampFlagStarredProjects();

  const gridColsStyle = isStarredProjectsEnabled
    ? PROJECT_COLUMN_STYLE_WITH_STARS
    : PROJECT_COLUMN_STYLE_NO_STARS;

  return (
    <li
      className={classNames(
        gridColsStyle,
        'group grid items-center gap-x-16 border-t-[1px] border-solid border-[#f4f4f4] px-[9px] py-4'
      )}
      data-test="paginated-list-item">
      {isLoading ? (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          {isStarredProjectsEnabled && <Skeleton />}
        </>
      ) : (
        <>
          <Link
            className="truncate p-3 hover:text-gold-550 focus:text-gold-550 focus:outline-transparent"
            to={`/${encodeURIComponent(
              itemData.entityName
            )}/${encodeURIComponent(itemData.name)}`}>
            {itemData.name}
          </Link>
          <div className="truncate p-3">
            {itemData.lastActive ? (
              LastActiveDate(itemData.lastActive)
            ) : (
              <span>-</span>
            )}
          </div>

          {projectAccess && (
            <div className="flex items-center truncate p-3">
              <Icon name={getIconForProjectAccess(projectAccess, true)} />
              <span className="ml-6">
                {getDisplayNameForProjectAccess(projectAccess, true)}
              </span>
            </div>
          )}

          <div className="truncate p-3">{itemData.totalRuns}</div>

          <ProjectActionMenu project={itemData} onDeleted={onDeleted} />
          {isStarredProjectsEnabled && <StarButton itemData={itemData} />}
        </>
      )}
    </li>
  );
};

const StarButton = ({itemData}: {itemData: ListItem}) => {
  const {starredProjects, onStarClick} = React.useContext(
    StarredProjectsContext
  );
  const isStarred = starredProjects[itemData.id] !== undefined;
  const hiddenStyle = isStarred ? '' : 'hidden group-hover:flex';
  return (
    <Button
      data-test={
        isStarred
          ? `star-filled-${itemData.name}`
          : `star-empty-${itemData.name}`
      }
      className={hiddenStyle}
      size="medium"
      variant="ghost"
      icon={isStarred ? 'star-filled' : 'star'}
      aria-label={isStarred ? 'Unstar project' : 'Star project'}
      onClick={() => {
        onStarClick(itemData, !isStarred, 'projects list');
      }}
    />
  );
};

const UserProjectListItem = ({
  isLoading,
  itemData,
  personalEntity,
}: {
  isLoading: boolean;
  itemData: ListItem;
  personalEntity?: string;
}) => {
  const projectAccess = getProjectAccess(
    itemData.access ?? AccessOption.Private
  );
  const isStarredProjectsEnabled = useRampFlagStarredProjects();

  const gridColsStyle = isStarredProjectsEnabled
    ? USER_PROJECT_COLUMN_STYLE_WITH_STARS
    : USER_PROJECT_COLUMN_STYLE_NO_STARS;

  const isTeam = itemData.entityName !== personalEntity;

  return (
    <li
      className={classNames(
        gridColsStyle,
        'group grid items-center gap-x-16 border-t-[1px] border-solid border-[#f4f4f4] px-[9px] py-4'
      )}
      data-test="paginated-list-item">
      {isLoading ? (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          {isStarredProjectsEnabled && <Skeleton />}
        </>
      ) : (
        <>
          <Link
            className="truncate p-3 hover:text-gold-550 focus:text-gold-550 focus:outline-transparent"
            to={`/${encodeURIComponent(
              itemData.entityName
            )}/${encodeURIComponent(itemData.name)}`}>
            {itemData.name}
          </Link>
          <div className="truncate p-3">
            {itemData.lastActive ? (
              LastActiveDate(itemData.lastActive)
            ) : (
              <span>-</span>
            )}
          </div>

          {projectAccess ? (
            <div className="flex items-center truncate p-3">
              <Icon name={getIconForProjectAccess(projectAccess, isTeam)} />
              <span className="ml-6">
                {getDisplayNameForProjectAccess(projectAccess, isTeam)}
              </span>
            </div>
          ) : (
            <span>-</span>
          )}

          <div className="truncate p-3">{itemData.totalRuns}</div>

          <Link
            className="truncate p-3 hover:text-gold-550 focus:text-gold-550 focus:outline-transparent"
            to={`/${encodeURIComponent(itemData.entityName)}`}>
            {itemData.entityName}
          </Link>

          <ProjectActionMenu project={itemData} />
          {isStarredProjectsEnabled && <StarButton itemData={itemData} />}
        </>
      )}
    </li>
  );
};
const UserProjectListHeader = ({
  sortOrder,
  setSortOrder,
}: {
  sortOrder: [SortOperators | null, string];
  setSortOrder: any;
}) => {
  const [sortDir, sortBy] = sortOrder;
  const columns = [
    {displayName: 'Name', orderBy: 'name', isActive: true},
    {displayName: 'Last Run', orderBy: 'last_active', isActive: true},
    {displayName: 'Project Visibility', orderBy: 'access', isActive: true},
    // ordering by total_runs is not supported in the backend so this cannot be set to active
    {displayName: 'Runs', orderBy: 'total_runs', isActive: false},
    {displayName: 'Entity', orderBy: 'entity_id', isActive: true},
  ];
  const isStarredProjectsEnabled = useRampFlagStarredProjects();
  const gridColsStyle = isStarredProjectsEnabled
    ? USER_PROJECT_COLUMN_STYLE_WITH_STARS
    : USER_PROJECT_COLUMN_STYLE_NO_STARS;

  return (
    <div
      className={classNames(
        gridColsStyle,
        'grid h-[32px] items-center gap-x-16 bg-[#f4f4f4] px-[9px] py-5 text-sm capitalize'
      )}>
      {columns.map((col, i) => {
        const isActiveCol = sortBy === col.orderBy;
        return (
          <button
            className={`flex p-3 text-left focus:font-bold focus:outline-none ${
              isActiveCol && 'font-bold'
            }`}
            disabled={!col.isActive}
            key={`project-list-header-${i}`}
            role="columnheader"
            onClick={e => {
              const newSortDir =
                sortDir === sortDirections.asc
                  ? sortDirections.desc
                  : sortDirections.asc;
              setSortOrder([newSortDir, col.orderBy]);
            }}>
            <span>{col.displayName}</span>{' '}
            {isActiveCol ? (
              sortDir === sortDirections.asc ? (
                <Icon height={18} name="sort-ascending" />
              ) : (
                <Icon name="sort-descending" />
              )
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

type ListItem = {
  id: string;
  name: string;
  entityName: string;
  access?: AccessOptions;
  totalRuns: number;
  lastActive: string;
  createdAt: string;
  __typename: 'Project';
};

export const UserProjectPaginatedList = makePaginatedList<
  ProjectsByUserQuery,
  ListItem
>(
  {
    EmptyState: ({
      isCreatingProject,
      toggleCreateProjectDrawer,
      defaultTeam,
      isReadOnly,
    }) => (
      <EmptyProjectsComp
        isCreatingProject={isCreatingProject}
        toggleCreateProjectDrawer={toggleCreateProjectDrawer}
        defaultTeam={defaultTeam}
        isTeam={false}
        isReadOnly={isReadOnly}
      />
    ),
    ListWrapper: (props: any) => <ul {...props} />,
    ListItem: UserProjectListItem,
    ListHeader: UserProjectListHeader,
    Meta: ProjectListMeta,
    Wrapper: ProjectListWrapper,
  },
  {
    extractListItems(data: ProjectsByUserQuery) {
      return (
        data?.user?.projects?.edges
          .map(e => e.node)
          .filter((n): n is ListItem => !!n) ??
        [].filter(
          (n: ListItem) =>
            !(n.entityName !== data?.user?.username && n.totalRuns === 0)
        )
      );
    },
    extractPersonalEntity(data: ProjectsByUserQuery) {
      return data?.user?.username ?? undefined;
    },
    extractPageInfo(data) {
      return data?.user?.projects?.pageInfo as PageInfo;
    },
    listSize: 20,
    orderBy: 'created_at',
    query: PROJECTS_BY_USER_QUERY,
  }
);

export const TeamProjectPaginatedList = makePaginatedList<
  ProjectsByTeamQuery,
  ListItem
>(
  {
    EmptyState: ({
      isCreatingProject,
      toggleCreateProjectDrawer,
      defaultTeam,
      isReadOnly,
    }) => (
      <EmptyProjectsComp
        isCreatingProject={isCreatingProject}
        toggleCreateProjectDrawer={toggleCreateProjectDrawer}
        defaultTeam={defaultTeam}
        isTeam={true}
        isReadOnly={isReadOnly}
      />
    ),
    ListWrapper: (props: any) => <ul {...props} />,
    ListItem: ProjectListItem,
    ListHeader: ProjectListHeader,
    Meta: ProjectListMeta,
    Wrapper: ProjectListWrapper,
  },
  {
    extractListItems(data: ProjectsByTeamQuery) {
      return (
        data?.projects?.edges
          .map(e => e.node)
          .filter((n): n is ListItem => !!n) ?? []
      );
    },
    extractPageInfo(data) {
      return data?.projects?.pageInfo as PageInfo;
    },
    listSize: 20,
    orderBy: 'created_at',
    query: PROJECTS_BY_TEAM_QUERY,
  }
);

export const AccountProjectPaginatedList = makePaginatedList<
  ProjectsByAccountQuery,
  ListItem
>(
  {
    EmptyState: ({
      isCreatingProject,
      toggleCreateProjectDrawer,
      defaultTeam,
      isReadOnly,
    }) => (
      <EmptyProjectsComp
        isCreatingProject={isCreatingProject}
        toggleCreateProjectDrawer={toggleCreateProjectDrawer}
        defaultTeam={defaultTeam}
        isTeam={false}
        isReadOnly={isReadOnly}
      />
    ),
    ListWrapper: (props: any) => <ul {...props} />,
    ListItem: ProjectListItem,
    ListHeader: ProjectListHeader,
    Meta: ProjectListMeta,
    Wrapper: ProjectListWrapper,
  },
  {
    extractListItems(data: ProjectsByAccountQuery) {
      return (
        data?.organization?.projects?.edges
          .map(e => e.node)
          .filter((n): n is ListItem => !!n) ?? []
      );
    },
    extractPageInfo(data) {
      return data?.organization?.projects?.pageInfo as PageInfo;
    },
    listSize: 20,
    orderBy: 'created_at',
    query: PROJECTS_BY_ACCOUNT_QUERY,
  }
);
